import { useEffect, useState } from "react";

import { listRoles } from "./roles.gateway";
import type { Role } from "./roles.types";

export const ROLE_OPTIONS_PAGE_SIZE = 100;
const MAX_ROLE_PAGES = 100;

export interface RoleOption {
  value: string;
  label: string;
}

export interface UseRoleOptionsResult {
  options: RoleOption[];
  isLoading: boolean;
  hasError: boolean;
}

function toOption(role: Role): RoleOption {
  return { value: role.id, label: role.nome || role.codigo };
}

async function fetchAllRoles(signal: AbortSignal): Promise<Role[]> {
  const collected: Role[] = [];

  for (let pagina = 1; pagina <= MAX_ROLE_PAGES; pagina += 1) {
    const { roles, total } = await listRoles({
      pagina,
      tamanhoPagina: ROLE_OPTIONS_PAGE_SIZE,
      signal,
    });

    collected.push(...roles);

    if (roles.length < ROLE_OPTIONS_PAGE_SIZE || collected.length >= total) break;
  }

  return collected;
}

export function useRoleOptions(): UseRoleOptionsResult {
  const [options, setOptions] = useState<RoleOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetchAllRoles(controller.signal)
      .then((roles) => {
        if (cancelled) return;
        setOptions(roles.map(toOption));
        setHasError(false);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return { options, isLoading, hasError };
}
